import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/DraftExecutionStatus.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const getFileName = (path) => path?.split("\\").pop();

const AllDrafts = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [createdByFilter, setCreatedByFilter] = useState("ALL");

  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  /* ================= FETCH ALL DRAFT OUTPUTS ================= */

  const fetchAllDrafts = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await fetch(`${API_BASE}/drafts/alldrafts`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch drafts");
      }

      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("FETCH ERROR:", err);
      setError("Unable to load drafts");
    } finally {
      setLoading(false);
    }
  };

  /* ================= INITIAL LOAD ================= */

  useEffect(() => {
    fetchAllDrafts();
  }, []);

/* ================= UPDATE STATUS ================= */

  const submitRow = async (row) => {
    try {
      const res = await fetch(`${API_BASE}/drafts/row-status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          job_batch_id: row.job_batch_id,
          input_file_row: row.input_file_row,
          job_status: "SUBMITTED",
          output_file: row.output_file,
          failed_reason: null,
        }),
      });

      if (!res.ok) throw new Error("Submit failed");

      alert("Draft submitted successfully");

      fetchAllDrafts(); // refresh

    } catch (err) {
      console.error(err);
      alert("Unable to submit draft");
    }
  };
  
  /* ================= FILE SELECTION ================= */

  const handleSelectOne = (filePath) => {
    setSelectedFiles((prev) =>
      prev.includes(filePath)
        ? prev.filter((f) => f !== filePath)
        : [...prev, filePath]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedFiles([]);
    } else {
      const allFiles = rows
        .filter((r) => r.output_file)
        .map((r) => r.output_file);

      setSelectedFiles(allFiles);
    }
    setSelectAll(!selectAll);
  };

  /* ================= DOWNLOAD ================= */

  const downloadFile = async (filePath) => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE}/drafts/download?path=${encodeURIComponent(filePath)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) {
        throw new Error("Download failed");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = getFileName(filePath);

      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("DOWNLOAD ERROR:", err);
      alert("Unable to download file");
    }
  };

  /* ================= MULTI DOWNLOAD ================= */

  const downloadMultiple = async (files) => {
    for (const file of files) {
      await downloadFile(file);
    }
  };

  const downloadSelected = () => {
    if (selectedFiles.length === 0) {
      alert("Please select at least one file");
      return;
    }

    downloadMultiple(selectedFiles);
  };

  const downloadAll = () => {
    const allFiles = rows
      .filter((r) => r.output_file)
      .map((r) => r.output_file);

    if (allFiles.length === 0) {
      alert("No files available");
      return;
    }

    downloadMultiple(allFiles);
  };

  /* ================= FILTER + PAGINATION ================= */

	// Get unique users for "Created By" dropdown
	const uniqueUsers = [
	  ...new Set(rows.map((d) => d.user_name).filter(Boolean)),
	];

	// Apply filters
	const filteredDrafts = rows.filter((draft) => {
	  const statusMatch =
		statusFilter === "ALL" || draft.job_status === statusFilter;

	  const userMatch =
		createdByFilter === "ALL" || draft.user_name === createdByFilter;

	  return statusMatch && userMatch;
	});

	// Pagination
	const totalPages = Math.ceil(filteredDrafts.length / pageSize);

	const paginatedDrafts = filteredDrafts.slice(
	  (currentPage - 1) * pageSize,
	  currentPage * pageSize
	);
	
  /* ================= RENDER ================= */

  return (
    <div className="execution-container">
      <h2>All Drafts</h2>

      {loading && <p>Loading drafts...</p>}
      {error && <p className="error">{error}</p>}

      {!loading && rows.length === 0 && (
        <p>No drafts found.</p>
      )}
	  
	  
	  {/* ===== FILTERS ===== */}
		<div className="draft-filters">

		  {/* Status Filter */}
		  <div>
			<label>Status:</label>
			<select
			  value={statusFilter}
			  onChange={(e) => {
				setStatusFilter(e.target.value);
				setCurrentPage(1);
			  }}
			>
			  <option value="ALL">All</option>
              <option value="PENDING">Pending</option>
              <option value="PROCESSING">Processing</option>
              <option value="COMPLETED">Completed</option>
              <option value="FAILED">Failed</option>
              <option value="SUBMITTED">Submitted</option>
			</select>
		  </div>

		  {/* Created By Filter */}
		  <div>
			<label>Created By:</label>
			<select
			  value={createdByFilter}
			  onChange={(e) => {
				setCreatedByFilter(e.target.value);
				setCurrentPage(1);
			  }}
			>
			  <option value="ALL">All</option>

			  {uniqueUsers.map((user) => (
				<option key={user} value={user}>
				  {user}
				</option>
			  ))}
			</select>
		  </div>

		  {/* Page Size */}
		  <div>
			<label>Show:</label>
			<select
			  value={pageSize}
			  onChange={(e) => {
				setPageSize(Number(e.target.value));
				setCurrentPage(1);
			  }}
			>
			  <option value={25}>25</option>
			  <option value={50}>50</option>
			  <option value={100}>100</option>
			</select>
		  </div>
		
		{/* ===== PAGINATION ===== */}
		{totalPages > 1 && (
		  <div className="pagination">

			<button
			  disabled={currentPage === 1}
			  onClick={() => setCurrentPage((p) => p - 1)}
			>
			  Prev
			</button>

			<span>
			  Page {currentPage} of {totalPages}
			</span>

			<button
			  disabled={currentPage === totalPages}
			  onClick={() => setCurrentPage((p) => p + 1)}
			>
			  Next
			</button>

		  </div>
		)}
		
		{/* ===== ACTION BUTTONS ===== */}
      <div className="execution-actions">
        <button
          className="secondary-btn"
          onClick={downloadSelected}
        >
          Download Selected
        </button>

        <button
          className="secondary-btn"
          onClick={downloadAll}
        >
          Download All
        </button>

        <button
          className="secondary-btn"
          onClick={() => navigate("/admin")}
        >
          Back to Draft Generator
        </button>
      </div>
	  
		</div>

      {/* ===== TABLE ===== */}
      {!loading && rows.length > 0 && (
        <table className="status-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
              </th>

              <th>Job Card ID</th>
              <th>Batch ID</th>
              <th>Identifier</th>
              <th>Title</th>
              <th>Status</th>
              <th>Fail Reason</th>
			  <th>Created By</th>
              <th>Created At</th>
              <th>Output File</th>
			  <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {paginatedDrafts.map((row) => (
              <tr key={row.job_card_id}>
                {/* Checkbox */}
                <td>
                  {row.output_file && (
                    <input
                      type="checkbox"
                      checked={selectedFiles.includes(row.output_file)}
                      onChange={() =>
                        handleSelectOne(row.output_file)
                      }
                    />
                  )}
                </td>

                <td>{row.job_card_id}</td>
                <td>{row.job_batch_id}</td>
                <td>{row.identifier}</td>
                <td>{row.title}</td>

                <td
                  style={{
                    color:
                      row.job_status === "COMPLETED"
                        ? "blue"
                        : row.job_status === "SUBMITTED"
                        ? "green"
						: row.job_status === "FAILED"
                        ? "red"
                        : "black",
                    fontWeight: 600,
                  }}
                >
                  {row.job_status}
                </td>

                <td>{row.failed_reason || "-"}</td>
				<td>{row.user_name}</td>
                <td>
                  {row.createdAt
                    ? new Date(row.createdAt).toLocaleString()
                    : "-"}
                </td>

                <td>
                  {row.output_file ? (
                    <button
                      className="link-btn"
                      onClick={() =>
                        downloadFile(row.output_file)
                      }
                    >
                      {getFileName(row.output_file)}
                    </button>
                  ) : (
                    "-"
                  )}
                </td>
				<td>

                  {row.job_status == "COMPLETED" && (
                    <button
                      className="secondary-btn"
                      onClick={() => submitRow(row)}
                    >
                      Submit
                    </button>
                  )}

                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AllDrafts;
